import { useState } from 'react';
import { describeError } from '../lib/api';
import { useRemoveMyPhoto, useSetMyPhoto, type MyProfile } from '../lib/queries';

/**
 * The controls for PRF-02's photograph. The picture itself is drawn by whatever card this sits in,
 * because that card already shows her face and two of them on one screen would be one too many.
 *
 * `accept="image/*"` rather than a list of types, because on a phone that is what turns the picker
 * into "take a photo" as well as "choose one" — and the API refuses anything it cannot open, so the
 * hint costs nothing to be generous with.
 *
 * The file input is hidden behind the button rather than styled: every browser draws its own, none
 * of them look like this kit, and a label wrapping a hidden input is the one arrangement that keeps
 * the keyboard and the screen reader working.
 */
export function PhotoPicker({ profile }: { profile: MyProfile }) {
  const [problem, setProblem] = useState<string | null>(null);
  const set = useSetMyPhoto();
  const remove = useRemoveMyPhoto();

  const busy = set.isPending || remove.isPending;

  const choose = (file: File | undefined) => {
    if (!file) {
      return;
    }

    setProblem(null);
    set.mutate(file, {
      onError: (error) => setProblem(describeError(error, 'That photo could not be uploaded.')),
    });
  };

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <label className="text-button rounded-button inline-flex min-h-touch cursor-pointer items-center border border-border-input bg-surface px-4 font-semibold text-ink-2">
          {busy ? 'Working…' : profile.photoAssetId ? 'Change photo' : 'Add a photo'}
          <input
            type="file"
            accept="image/*"
            disabled={busy}
            className="sr-only"
            onChange={(event) => {
              choose(event.target.files?.[0]);
              // Cleared so that picking the same file twice — after a failure — fires again.
              event.target.value = '';
            }}
          />
        </label>

        {profile.photoAssetId && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setProblem(null);
              remove.mutate(undefined, {
                onError: (error) =>
                  setProblem(describeError(error, 'That photo could not be removed.')),
              });
            }}
            className="text-subtitle min-h-touch font-semibold text-ink-3 underline disabled:opacity-60"
          >
            Remove
          </button>
        )}
      </div>

      {/* PRF-05's promise, kept in front of her at the moment she decides. */}
      <p className="text-eyebrow mt-1.5 text-ink-4">Only people signed in to the app can see it.</p>

      {problem && (
        <p role="alert" className="text-subtitle mt-2 text-alert-text">
          {problem}
        </p>
      )}
    </div>
  );
}
